/** User-facing message when Turnstile times out or needs a fresh challenge. */
export const TURNSTILE_TIMEOUT_MESSAGE =
  "Security check timed out. Retrying automatically — or click Retry if it does not refresh.";

/** User-facing message after automatic retries are exhausted. */
export const TURNSTILE_RETRY_FAILED_MESSAGE =
  "Security check could not complete. Check your connection, disable ad blockers, then click Retry.";

const RETRIABLE_ERROR_CODES = new Set(["600010", "600001", "106010", "300030", "300031", "300032"]);

const TIMEOUT_ERROR_PATTERNS = [/timed?\s*out/i, /verification timed out/i, /timeout/i];

/** Errors from PrivateToken / challenge-platform probes — safe to ignore in UI. */
export function isIgnorableTurnstileError(error: string): boolean {
  if (!error) {
    return false;
  }
  return (
    error.includes("401") ||
    error.includes("challenge-platform") ||
    error.includes("PrivateToken") ||
    error.toLowerCase().includes("unauthorized")
  );
}

/**
 * Is Retriable Turnstile Error.
 */
export function isRetriableTurnstileError(error: string): boolean {
  if (!error || isIgnorableTurnstileError(error)) {
    return false;
  }

  const normalized = error.trim();
  if (RETRIABLE_ERROR_CODES.has(normalized)) {
    return true;
  }

  return TIMEOUT_ERROR_PATTERNS.some((pattern) => pattern.test(normalized));
}

/** Map Cloudflare error codes to readable copy for end users. */
export function formatTurnstileError(error: string): string {
  if (!error) {
    return TURNSTILE_RETRY_FAILED_MESSAGE;
  }

  if (isIgnorableTurnstileError(error)) {
    return TURNSTILE_RETRY_FAILED_MESSAGE;
  }

  if (TIMEOUT_ERROR_PATTERNS.some((pattern) => pattern.test(error))) {
    return TURNSTILE_TIMEOUT_MESSAGE;
  }

  if (RETRIABLE_ERROR_CODES.has(error.trim())) {
    return TURNSTILE_RETRY_FAILED_MESSAGE;
  }

  // Avoid showing raw numeric codes (e.g. 600010) to users.
  if (/^\d{5,6}$/.test(error.trim())) {
    return TURNSTILE_RETRY_FAILED_MESSAGE;
  }

  return error;
}
